import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Select, Spin } from 'antd';

import { useGetCitiesBySearchQuery } from 'lib.store/src/main/cities/cities.api';
import { INIT_CURRENT_PAGE } from 'lib.shared/src/constants';

type SelectOption = { label: string; value: string };

interface SuggestSelectCitiesProps {
  value: {
    id: number;
    slug: string;
    country?: {
      id: number;
      slug: string;
    };
  } | null;
  countryValue: {
    id: number;
    slug: string;
  } | null;
  onChange?: (
    value: {
      id: number;
      slug: string;
    } | null,
  ) => void;
  placeholder: string;
  'data-test-id'?: string;
}

const SuggestSelectCities: React.FC<SuggestSelectCitiesProps> = ({
  value,
  countryValue,
  onChange,
  placeholder,
  'data-test-id': dataTestId,
}) => {
  const [citySearchValue, setCitySearchValue] = useState<string>('');
  const [page, setPage] = useState(INIT_CURRENT_PAGE);
  const [citiesOptions, setCitiesOptions] = useState<SelectOption[]>([]);

  const shouldFetch = citySearchValue.length >= 3;

  const {
    data: cities,
    isLoading: citiesLoading,
    isFetching: isCitiesFetching,
  } = useGetCitiesBySearchQuery(
    {
      search: shouldFetch ? citySearchValue : '',
      countries: countryValue?.id ? countryValue.id.toString() : '',
      page,
    },
    {
      skip: !shouldFetch,
    },
  );

  useEffect(() => {
    setPage(INIT_CURRENT_PAGE);
  }, [countryValue]);

  useEffect(() => {
    if (!shouldFetch || !cities?.data) {
      if (value && value.slug) {
        setCitiesOptions([{ value: value.id.toString(), label: value.slug }]);
      } else {
        setCitiesOptions([]);
      }
      return;
    }
    const mapped = cities.data.map(({ slug, id }) => ({
      value: id.toString(),
      label: slug,
    }));

    setCitiesOptions((prev) => {
      let options: SelectOption[];
      if (page === INIT_CURRENT_PAGE) {
        options = mapped;
      } else {
        const byValue = new Map(prev.map((o) => [o.value, o]));
        mapped.forEach((o) => byValue.set(o.value, o));
        options = [...byValue.values()];
      }
      if (value && value.slug) {
        const valueStr = value.id.toString();
        const existsInOptions = options.some((o) => o.value === valueStr);
        if (!existsInOptions) {
          options = [{ value: valueStr, label: value.slug }, ...options];
        }
      }
      return options;
    });
  }, [cities, page, shouldFetch, value]);

  const onPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 2;
      const lastPage = cities?.meta?.last_page ?? 0;
      if (nearBottom && page < lastPage && !citiesLoading && !isCitiesFetching) {
        setPage((p) => p + 1);
      }
    },
    [page, cities?.meta?.last_page, citiesLoading, isCitiesFetching],
  );

  const isLoading = citiesLoading || isCitiesFetching;

  const handleChange = useCallback(
    (selectedValue: string | null, option: { value: string; label: string } | undefined) => {
      if (!selectedValue || !option) {
        onChange?.(null);
        return;
      }
      onChange?.({
        id: Number(selectedValue),
        slug: option.label,
      });
      setCitySearchValue('');
    },
    [onChange],
  );

  const selectValue = useMemo(() => {
    if (!value) return undefined;
    if (value.country && value.country.id !== countryValue?.id) return undefined;
    return value.id.toString();
  }, [value, countryValue]);

  const notFoundContent = useMemo(() => {
    if (isLoading) return <Spin size="small" />;
    if (!shouldFetch) return <div>Enter at least 3 characters to search</div>;
    return <div>There is no cities</div>;
  }, [isLoading, shouldFetch]);
  return (
    <Select
      id="suggest-select-cities"
      placeholder={placeholder}
      style={{ width: '100%' }}
      filterOption={false}
      notFoundContent={notFoundContent}
      loading={isLoading}
      dropdownStyle={{ maxHeight: 190 }}
      options={citiesOptions}
      value={selectValue}
      onChange={(val) => {
        const option = citiesOptions.find((o) => o.value === val);
        handleChange(val ?? null, option);
      }}
      onSearch={(val) => {
        setCitySearchValue(val || '');
        setPage(INIT_CURRENT_PAGE);
      }}
      onOpenChange={(open) => {
        if (!open) {
          setCitySearchValue('');
          setPage(INIT_CURRENT_PAGE);
        }
      }}
      searchValue={citySearchValue}
      allowClear
      showSearch
      data-test-id={dataTestId}
      onPopupScroll={onPopupScroll}
    />
  );
};

export default SuggestSelectCities;
